import {
  GridCallbackDetails,
  GridColDef,
  GridRowEditStopParams,
  GridRowEditStopReasons,
  GridRowModes,
  GridRowModesModel,
  MuiEvent,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import { useMutationState } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import { spacing } from 'ext-ui/style/spacing'
import ReceiptRulesUpsellSvg from 'ext-ui/svg/illustration/ReceiptRulesUpsellSvg'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { BackendErrorToastMessage } from 'src/app/Toast/Toast'
import Box from 'src/components/Box/Box'
import { chevronColumnDef } from 'src/components/DataGrid/cells/ChevronCell'
import DataGridCellWrapper from 'src/components/DataGrid/cells/DataGridCellWrapper/DataGridCellWrapper'
import DataGridEditableCell from 'src/components/DataGrid/cells/DataGridEditableCell/DataGridEditableCell'
import TextFieldCell from 'src/components/DataGrid/cells/TextFieldCell/TextFieldCell'
import DataGrid from 'src/components/DataGrid/DataGrid'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import OnOffSwitch from 'src/components/OnOffSwitch/OnOffSwitch'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import SaasUpgradeOrContent from 'src/feature/SaasUpgradeOrContent/SaasUpgradeOrContent'
import {
  FormFieldsValidation,
  useBackgroundDispatch,
  useFormFields,
} from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { UserFeature } from 'src/models/user.models'
import { connectedPush } from 'src/navigation/Lnk'
import { customFieldDetailUrl } from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import {
  AdditionalFieldsResponse,
  PatchAdditionalFieldRequest,
  PatchAdditionalFieldVariables,
  PostAdditionalFieldRequest,
  usePatchAdditionalField,
  usePostAdditionalField,
  useQueryAdditionalFields,
} from 'src/queries/organizations/additionalFields.queries'
import { orgQueryKeys } from 'src/queries/queries.constants'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectOrganizationIdFromRoute } from 'src/redux/selectors/data/organizations.selectors'
import { selectLoadedFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import { selectBrandColorHighlight } from 'src/redux/selectors/session.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { BackendErrorPayload } from 'src/utils/error.utils'
import {
  stringIsEmpty,
  stringLengthIsLessThan,
} from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

const NEW_ADDITIONAL_FIELD_ID = 'NEW_ADDITIONAL_FIELD_ID'

// Additional field row type with index for DataGrid
type AdditionalFieldRow = AdditionalFieldsResponse & {
  index: number
}

// Create new row function
const NewAdditionalFieldRow = (): AdditionalFieldRow => ({
  id: NEW_ADDITIONAL_FIELD_ID,
  name: '',
  active: true,
  required: false,
  editable: true,
  index: 0,
})

interface ExpenseRequirementsAdditionalFieldsProps {
  addingNewField?: boolean
  setAddingNewField?: (adding: boolean) => void
}

const ExpenseRequirementsAdditionalFields: React.FC<
  ExpenseRequirementsAdditionalFieldsProps
> = (props) => {
  const translate = useTranslate()
  const dispatch = useBackgroundDispatch()
  const { analytics } = useAnalyticsContext()
  const apiRef = useGridApiRef()
  const currentUser = useCurrentUser()
  const loadedRemoteConfig = useSelector(selectLoadedFromRemoteConfigState)
  const appName = useSelector(selectAppName)
  const brandColorHighlight = useSelector(selectBrandColorHighlight)
  const {
    enable_custom_fields_expense_fields: customFieldsExpenseFieldsEnabled,
  } = useSelector(selectFeatureFlaggerFromState) ?? {}
  const additionalFieldsLabel = customFieldsExpenseFieldsEnabled
    ? 'Custom Fields'
    : 'Additional Fields'
  const organizationId = useSelector(selectOrganizationIdFromRoute)

  // State for managing row edit modes
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({})

  // Fetch additional fields data
  const {
    data: additionalFields,
    isFetching,
    isSuccess,
  } = useQueryAdditionalFields({ filters: {} })

  // Post mutation for creating new fields
  const { mutateAsync: postAdditionalField, isPending: postLoading } =
    usePostAdditionalField()

  // Patch mutation for updating fields
  const { mutateAsync: patchAdditionalField } = usePatchAdditionalField()
  const patchAdditionalFieldPendingMutations = useMutationState({
    filters: { mutationKey: orgQueryKeys.patchAdditionalField() },
    // TS can't know the type just from the mutationKey
    // https://github.com/TanStack/query/issues/6843#issuecomment-1941577758
    select: (mutation) =>
      mutation.state.status === 'pending'
        ? (mutation.state.variables as
            | PatchAdditionalFieldVariables
            | undefined)
        : undefined,
  }).filter(
    (variables): variables is PatchAdditionalFieldVariables => !!variables,
  )

  const { EDIT_ADDITIONAL_FIELDS: hasEditAdditionalFieldsPermissions } =
    useQueryUserFeatures([UserFeature.EDIT_ADDITIONAL_FIELDS]).data || {}

  // PATCH /additionalfields doesn't enrich mccGroups in its response, so fall
  // back to the cached value when reporting analytics — name/active/required
  // edits don't change the MCC set.
  const cachedMccGroups = (id: string): string[] =>
    additionalFields?.find((f) => f.id === id)?.mccGroups ?? []

  // Show toast if patch fails
  const patchSucceeded = async ({
    id,
    body,
  }: {
    id: string
    body: PatchAdditionalFieldRequest
  }): Promise<boolean> => {
    return await patchAdditionalField({ id, body })
      .then((updated) => {
        analytics?.track('update custom field', {
          name: updated.name,
          required: updated.required,
          applied_merchant_categories: updated.mccGroups ?? cachedMccGroups(id),
        })
        return true
      })
      .catch((e: AxiosError<BackendErrorPayload<string>>) => {
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: <BackendErrorToastMessage error={e} />,
            failureColorScheme: true,
            hideImage: true,
          }),
        )
        return false
      })
  }

  // Form field validation for inline editing
  const {
    fields: additionalFieldsFields,
    setFields: setAdditionalFields,
    fieldsErrors: additionalFieldErrors,
    setFieldsErrors,
    hasFieldsErrors,
    setErrorsAndReturnDidPassValidation,
  } = useFormFields<PostAdditionalFieldRequest>(
    {
      name: '',
    },
    (): FormFieldsValidation<PostAdditionalFieldRequest> => ({
      name: [stringIsEmpty(), stringLengthIsLessThan(255)],
    }),
  )

  // new field row does not go through processRowUpdate the same way because it is manipulated via the DataGrid api
  // so we handle it separately here
  const handleRowEditStop = (
    params: GridRowEditStopParams<AdditionalFieldRow>,
    event: MuiEvent,
  ): void => {
    /**
     * If a row is not visible when the user moves from editing one cell to another (i.e. from scrolling to another part
     * of the table, resulting in the previous row being optimized a way via virtualization) then instead of exiting
     * edit mode in the usual way it triggers a handleRowEditStop event with reason 'rowFocusOut'
     *
     * this catches all cases where the user is not trying to save the row by pressing enter and prevents the default
     * behaviour of exiting edit mode and saving changes
     */
    if (params.reason !== GridRowEditStopReasons.enterKeyDown) {
      event.defaultMuiPrevented = true
      apiRef.current?.stopRowEditMode({
        id: params.id,
        ignoreModifications: true,
      })
    }

    // only handle new field case here
    if (params.id !== NEW_ADDITIONAL_FIELD_ID) return

    event.defaultMuiPrevented = true

    if (params.reason === GridRowEditStopReasons.enterKeyDown) {
      if (hasFieldsErrors || !setErrorsAndReturnDidPassValidation()) return
      postAdditionalField({
        body: { name: additionalFieldsFields.name, active: true },
      })
        .then((created) => {
          analytics?.track('create custom field', {
            name: created.name,
            required: created.required,
            applied_merchant_categories: created.mccGroups ?? [],
          })
          setAdditionalFields({ name: '' })
          apiRef.current?.setEditCellValue({
            id: NEW_ADDITIONAL_FIELD_ID,
            field: 'name',
            value: '',
          })
        })
        .catch((e: AxiosError<BackendErrorPayload<string>>) => {
          setFieldsErrors({ name: [e.response?.data.error ?? e.message] })
        })
        .finally(() => {
          apiRef.current?.setCellFocus(NEW_ADDITIONAL_FIELD_ID, 'name')
        })
    } else {
      props.setAddingNewField?.(false)
    }
  }

  // Keep non-editable fields at the top while preserving API order within groups.
  const rows = useMemo(() => {
    const existingRows = (additionalFields ?? [])
      .map((field, i) => ({
        ...field,
        index: i,
      }))
      .sort((a, b) => {
        if (a.editable === b.editable) return a.index - b.index
        return a.editable ? 1 : -1
      })

    if (!props.addingNewField) return existingRows
    if (existingRows.length === 0) return [NewAdditionalFieldRow()]

    const firstEditableIndex = existingRows.findIndex((row) => row.editable)
    const insertIndex =
      firstEditableIndex === -1 ? existingRows.length : firstEditableIndex

    return [
      ...existingRows.slice(0, insertIndex),
      NewAdditionalFieldRow(),
      ...existingRows.slice(insertIndex),
    ]
  }, [additionalFields, props.addingNewField])

  const handleRowModesModelChange = useCallback(
    (newRowModesModel: GridRowModesModel, _: GridCallbackDetails): void => {
      // handle edit status of new field row separately
      delete newRowModesModel[NEW_ADDITIONAL_FIELD_ID]
      setRowModesModel((old) => ({ ...old, ...newRowModesModel }))
    },
    [setRowModesModel],
  )

  // manually handle new field row's edit status
  useEffect(() => {
    if (props.addingNewField) {
      setRowModesModel((currentRowModesModel) => ({
        ...currentRowModesModel,
        [NEW_ADDITIONAL_FIELD_ID]: { mode: GridRowModes.Edit },
      }))
      apiRef.current?.setCellFocus(NEW_ADDITIONAL_FIELD_ID, 'name')
    } else {
      setRowModesModel((currentRowModesModel) => ({
        ...currentRowModesModel,
        [NEW_ADDITIONAL_FIELD_ID]: {
          mode: GridRowModes.View,
          ignoreModifications: true,
        },
      }))
      setAdditionalFields({ name: '' })
      setFieldsErrors({})
    }
  }, [props.addingNewField])

  const columns = useMemo(() => {
    const cols: Array<GridColDef<AdditionalFieldRow>> = [
      {
        field: 'name',
        headerName: translate('Field Name'),
        disableColumnMenu: true,
        sortable: false,
        hideable: false,
        editable: hasEditAdditionalFieldsPermissions,
        flex: 1,
        minWidth: 200,
        renderCell: (params: any): React.ReactElement => {
          return (
            <DataGridEditableCell
              value={params.row.name}
              params={params}
              editMode={'row'}
              type={'text'}
              tightTextClickArea={customFieldsExpenseFieldsEnabled}
              tooltipContent={
                !hasEditAdditionalFieldsPermissions
                  ? translate('Cannot edit name')
                  : !params.row.editable
                    ? translate('Cannot edit name')
                    : translate('Click to edit the field name')
              }
            />
          )
        },
        renderEditCell: (params: any): React.ReactElement => {
          return (
            <TextFieldCell
              characterLimit={255}
              tooltip={translate('Edit field name')}
              placeholder={translate('Enter field name')}
              microcopy={translate('Press Enter to save or Esc to cancel')}
              params={params}
              editMode={'row'}
              errorText={additionalFieldErrors.name?.[0]}
              clearError={(): void => setFieldsErrors({})}
              setValue={(val: string): void =>
                setAdditionalFields({ name: val })
              }
              disabled={
                !params.row.editable ||
                patchAdditionalFieldPendingMutations.some(
                  (variables) => variables.id === params.row.id,
                ) ||
                postLoading
              }
            />
          )
        },
      },
      {
        field: 'active',
        headerName: translate('Active'),
        description: translate(
          'When active, everyone in the org can see and fill out this field. When inactive, the field will be hidden from view.',
        ),
        disableColumnMenu: true,
        sortable: false,
        hideable: false,
        editable: false,
        minWidth: 128,
        renderCell: (params: any): React.ReactElement => {
          // handle new field row
          if (params.row.id === NEW_ADDITIONAL_FIELD_ID) return <></>

          return (
            <DataGridCellWrapper params={params}>
              <OnOffSwitch
                testId={'isActiveSwitch'}
                onBgColor={brandColorHighlight}
                smallMode
                defaultOnOffState={params.row.active}
                onChange={async (): Promise<boolean> =>
                  patchSucceeded({
                    id: params.row.id,
                    body: { active: !params.row.active },
                  })
                }
                disabled={
                  !params.row.editable ||
                  !hasEditAdditionalFieldsPermissions ||
                  patchAdditionalFieldPendingMutations.some(
                    (variables) => variables.id === params.row.id,
                  )
                }
              />
            </DataGridCellWrapper>
          )
        },
      },
      {
        field: 'required',
        headerName: translate('Required'),
        description: translate(
          'If required, this field must be completed on transactions.',
        ),
        disableColumnMenu: true,
        sortable: false,
        hideable: false,
        editable: false,
        minWidth: 128,
        renderCell: (params: any): React.ReactElement => {
          // handle new field row
          if (params.row.id === NEW_ADDITIONAL_FIELD_ID) return <></>

          return (
            <DataGridCellWrapper params={params}>
              <Checkbox
                data-testid={'isRequiredCheckbox'}
                aria-label={translate('Required Checkbox')}
                checked={params.row.required}
                disabled={
                  !params.row.active ||
                  !hasEditAdditionalFieldsPermissions ||
                  patchAdditionalFieldPendingMutations.some(
                    (variables) => variables.id === params.row.id,
                  )
                }
                onClick={async (e): Promise<void> => {
                  e.stopPropagation()
                  patchSucceeded({
                    id: params.row.id,
                    body: {
                      required: !params.row.required,
                    },
                  })
                }}
              />
            </DataGridCellWrapper>
          )
        },
      },
      customFieldsExpenseFieldsEnabled && chevronColumnDef,
    ].filter(Boolean) as Array<GridColDef<AdditionalFieldRow>>

    return cols
  }, [
    translate,
    brandColorHighlight,
    postLoading,
    hasEditAdditionalFieldsPermissions,
    customFieldsExpenseFieldsEnabled,
  ])

  if (!loadedRemoteConfig) {
    return <LoadingBackgroundPage />
  }

  if (!currentUser?.hasOrgBookkeeperPermissions) {
    return <NotFoundBackgroundPageContent />
  }

  return (
    <>
      <Helmet>
        <title>{`${additionalFieldsLabel} | ${appName}`}</title>
        <meta
          name="description"
          content={`${additionalFieldsLabel} page of ${appName}`}
        />
      </Helmet>
      <SaasUpgradeOrContent
        illustration={<ReceiptRulesUpsellSvg />}
        title={translate(additionalFieldsLabel)}
        description={translate(
          'Shorten review cycles and improve compliance. Require key details like attendees, travel class, or business purpose so expenses are complete the first time.',
        )}
        ctaDetailSrc={
          'Upgrade to ${tier} today to reduce back-and-forth and keep reviews and reconciliation moving smoothly.'
        }
        feature={UserFeature.VIEW_ADDITIONAL_FIELDS}
      >
        <Box
          css={css`
            display: flex;
            flex-direction: column;
            overflow: hidden;
          `}
          noPadding
          noShadow
          outlineBorder
        >
          <DataGrid
            apiRef={apiRef}
            rows={rows}
            columns={columns}
            filtersBody={{}}
            disableColumnFilter
            disableColumnReorder
            onCellClick={
              customFieldsExpenseFieldsEnabled && organizationId
                ? (params): void => {
                    if (params.row.id === NEW_ADDITIONAL_FIELD_ID) return
                    if (apiRef.current?.getRowMode(params.id) === 'edit') return
                    dispatch(
                      connectedPush(
                        customFieldDetailUrl(
                          organizationId,
                          params.row.id.toString(),
                        ),
                      ),
                    )
                  }
                : undefined
            }
            isCellEditable={(params): boolean => {
              return params.colDef.field === 'name' && params.row.editable
            }}
            rowModesModel={rowModesModel}
            onRowModesModelChange={handleRowModesModelChange}
            onRowEditStop={handleRowEditStop}
            processRowUpdate={async (
              updatedRow,
            ): Promise<AdditionalFieldRow> => {
              // Skip processing for new fields - they're handled in handleRowEditStop
              if (updatedRow.id === NEW_ADDITIONAL_FIELD_ID) return updatedRow
              if (!updatedRow.editable) return updatedRow

              // check here for errors for existing requirements
              if (hasFieldsErrors || !setErrorsAndReturnDidPassValidation())
                return Promise.reject('FRONT_END_VALIDATION')

              const result = await patchAdditionalField({
                id: updatedRow.id,
                body: { name: updatedRow.name },
              })
              analytics?.track('update custom field', {
                name: result.name,
                required: result.required,
                applied_merchant_categories:
                  result.mccGroups ?? cachedMccGroups(updatedRow.id),
              })
              return {
                ...result,
                index: updatedRow.index,
              }
            }}
            onProcessRowUpdateError={(
              e: string | AxiosError<BackendErrorPayload<string>>,
            ): void => {
              // handle case where setErrorsAndReturnDidPassValidation already handled setting front-end validation errors
              if (typeof e === 'string') return
              setFieldsErrors({ name: [e.response?.data.error ?? e.message] })
            }}
            infiniteScrollProps={{
              // no actual infinite scroll, but want loading behaviour
              fetch: (): void => {
                return
              },
              loading: isFetching,
              complete: isSuccess,
            }}
            sx={{
              borderTop: 'none', // Remove top border since Box already provides one
              '.MuiDataGrid-cell': {
                // Remove vertical borders between data cells
                borderRight: 'none',
                padding: `${spacing(2)} 0`,
              },
              '.MuiDataGrid-cell.MuiDataGrid-cell--editing': {
                padding: `${spacing(2)} 0`,
              },
            }}
            slotProps={{
              loadingOverlay: {
                // workaround for having non-0 height when loading on a non-fixed height datagrid
                noRowsVariant: 'skeleton',
              },
            }}
          />
        </Box>
      </SaasUpgradeOrContent>
    </>
  )
}

export default ExpenseRequirementsAdditionalFields
